import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Barcode from 'react-barcode';
import {
  Pane,
  PaneHeader,
  PaneMenu,
  IconButton,
  Button,
  Row,
  Col,
  Card,
  Badge,
  Icon,
  Callout,
  MultiColumnList,
  Checkbox,
  Select,
} from '@folio/stripes/components';
import RealBarcodeGenerator from './RealBarcodeGenerator'; // kware: إضافة مولد الباركود الحقيقي


import UserFriendlyPreview from './UserFriendlyPreview';
import ProfessionalBarcodePreview from './ProfessionalBarcodePreview';
import PrintSettings from './PrintSettings';
import PrinterDetection from './PrinterDetection';
import PrinterStatusMonitor from './PrinterStatusMonitor'; // kware: إضافة مراقب حالة الطابعات

const EnhancedBarcodeInterface = ({
  items,
  instance,
  selectedItems,
  onItemSelection,
  printSettings,
  onSettingsChange,
  onPrint,
  isPrinting,
  onClose,
  selectedPrinter,
  onPrinterSelected,
  discoveredPrinters = [], // kware: قائمة الطابعات المكتشفة
  onPrintersDiscovered, // kware: معالج اكتشاف الطابعات
}) => {
  const intl = useIntl();
  const callout = useRef();
  const barcodeGenerator = useRef(new RealBarcodeGenerator()); // kware: إضافة مولد الباركود الحقيقي
  const [activeTab, setActiveTab] = useState('preview');
  const [previewType, setPreviewType] = useState('user');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showPrinterDetection, setShowPrinterDetection] = useState(false);

  // Statistics
  const totalLabels = selectedItems.length * printSettings.copies;
  const estimatedTime = Math.ceil(totalLabels * 0.1); // 0.1 minutes per label

  const handleSelectAll = () => {
    const itemsWithBarcodes = items.filter((item) => item.barcode);
    onItemSelection(itemsWithBarcodes, true);
  };

  const handleDeselectAll = () => {
    onItemSelection([], false);
  };

  const getConnectionStatus = () => {
    if (printSettings.printerMode === 'browser') return 'browser';
    if (printSettings.printerMode === 'download') return 'download';
    if (selectedPrinter) return 'connected';
    return 'disconnected';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'browser': return 'primary';
      case 'download': return 'default';
      default: return 'red';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'check';
      case 'browser': return 'globe';
      case 'download': return 'download';
      default: return 'exclamation';
    }
  };

  const renderHeader = () => (
    <PaneHeader
      paneTitle={
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcodePrinting.enhanced.title"
          defaultMessage="Print Barcodes - {instanceTitle}"
          values={{ instanceTitle: instance?.title || 'Loading...' }}
        />
      }
      lastMenu={
        <PaneMenu>
          <IconButton
            icon="times"
            onClick={onClose}
            aria-label={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.close',
              defaultMessage: 'Close',
            })}
          />
        </PaneMenu>
      }
    />
  );

  const renderStatsBar = () => (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '12px 20px',
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}
    >
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
        <Badge color="primary">
          <Icon icon="tag" size="small" style={{ marginRight: '4px' }} />
          {selectedItems.length} {intl.formatMessage({
            id: 'ui-plugin-inventory-templates.barcodePrinting.enhanced.itemsSelected',
            defaultMessage: 'items selected'
          })}
        </Badge>

        <Badge color="default">
          <Icon icon="duplicate" size="small" style={{ marginRight: '4px' }} />
          {totalLabels} {intl.formatMessage({
            id: 'ui-plugin-inventory-templates.barcodePrinting.enhanced.totalLabels',
            defaultMessage: 'total labels'
          })}
        </Badge>

        <Badge color="default">
          <Icon icon="clock" size="small" style={{ marginRight: '4px' }} />
          ~{estimatedTime} {intl.formatMessage({
            id: 'ui-plugin-inventory-templates.barcodePrinting.enhanced.minutes',
            defaultMessage: 'min'
          })}
        </Badge>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcodePrinting.enhanced.printerStatus"
          defaultMessage="Printer:"
        />
        <Badge color={getStatusColor(getConnectionStatus())}>
          <Icon
            icon={getStatusIcon(getConnectionStatus())}
            size="small"
            style={{ marginRight: '4px' }}
          />
          {getConnectionStatus() === 'connected' ? selectedPrinter?.name :
            getConnectionStatus() === 'browser' ? 'Browser' :
              getConnectionStatus() === 'download' ? 'Download' : 'Not Connected'}
        </Badge>
      </div>
    </div>
  );

  const renderItemsTable = () => {
    const columnMapping = {
      selected: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.columns.selected',
        defaultMessage: 'Select',
      }),
      barcode: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.columns.barcode',
        defaultMessage: 'Barcode',
      }),
      materialType: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.columns.materialType',
        defaultMessage: 'Material Type',
      }),
      location: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.columns.location',
        defaultMessage: 'Location',
      }),
      callNumber: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.columns.callNumber',
        defaultMessage: 'Call Number',
      }),
    };

    const formatter = {
      selected: (item) => (
        <Checkbox
          checked={selectedItems.some((selected) => selected.id === item.id)}
          onChange={(e) => onItemSelection(item, e.target.checked)}
        />
      ),
      barcode: (item) => {
        // kware: استبدال react-barcode بـ RealBarcodeGenerator للحصول على باركود حقيقي
        const canvas = barcodeGenerator.current.generateBarcodeElement(
          item.barcode || 'N/A',
          printSettings.format || 'CODE128',
          {
            width: 1,
            height: 30,
            displayValue: false,
            margin: 0,
            background: '#ffffff',
            lineColor: '#000000'
          }
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px' }}>
            {/* Visual Barcode */}
            <div style={{ marginBottom: '4px' }}>
              <img
                src={canvas.toDataURL()}
                alt={item.barcode || 'N/A'}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
            {/* Barcode Text */}
            <div style={{
              fontSize: '11px',
              fontFamily: 'monospace',
              color: '#333',
              textAlign: 'center'
            }}
            >
              {item.barcode || 'N/A'}
            </div>
          </div>
        );
      },
      materialType: (item) => item.materialType?.name || 'N/A',
      location: (item) => item.effectiveLocation?.name || 'N/A',
      callNumber: (item) => item.callNumber || 'N/A',
    };

    const itemsWithBarcodes = items.filter((item) => item.barcode);

    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}
        >
          <h4 style={{ margin: 0 }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.enhanced.itemsWithBarcodes"
              defaultMessage="Items with Barcodes ({count})"
              values={{ count: itemsWithBarcodes.length }}
            />
          </h4>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              buttonStyle="default"
              onClick={handleSelectAll}
              disabled={itemsWithBarcodes.length === 0}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.selectAll"
                defaultMessage="Select All"
              />
            </Button>
            <Button
              buttonStyle="default"
              onClick={handleDeselectAll}
              disabled={selectedItems.length === 0}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.deselectAll"
                defaultMessage="Deselect All"
              />
            </Button>
          </div>
        </div>

        {itemsWithBarcodes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Icon icon="tag" size="large" style={{ marginBottom: '16px' }} />
            <div>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.noItemsWithBarcodes"
                defaultMessage="No items with barcodes found for this instance."
              />
            </div>
          </div>
        ) : (
          <MultiColumnList
            id="items-list"
            contentData={itemsWithBarcodes}
            columnMapping={columnMapping}
            formatter={formatter}
            visibleColumns={[
              'selected',
              'barcode',
              'materialType',
              'location',
              'callNumber',
            ]}
            interactive={false}
          />
        )}
      </div>
    );
  };


  const renderSettingsPanel = () => (
    <div style={{ padding: '20px' }}>
      {/* Print Settings */}
      <PrintSettings
        settings={printSettings}
        onSettingsChange={onSettingsChange}
      />

      {/* Printer Detection */}
      {printSettings.printerMode === 'professional' && (
        <div style={{ marginTop: '16px' }}>
          <Button
            buttonStyle="default"
            fullWidth
            onClick={() => setShowPrinterDetection(!showPrinterDetection)}
          >
            <Icon icon="desktop" size="small" style={{ marginRight: '4px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.togglePrinterDetection"
              defaultMessage="Printer Setup"
            />
          </Button>

          {showPrinterDetection && (
            <div style={{ marginTop: '8px' }}>
              <PrinterDetection
                onPrinterSelected={onPrinterSelected}
                selectedPrinter={selectedPrinter}
                discoveredPrinters={discoveredPrinters} // kware: تمرير الطابعات المكتشفة
                onPrintersDiscovered={onPrintersDiscovered} // kware: تمرير معالج الاكتشاف
              />
            </div>
          )}
        </div>
      )}

      {/* kware: إضافة مراقب حالة الطابعات */}
      {discoveredPrinters.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <PrinterStatusMonitor
            printers={discoveredPrinters}
            compact
          />
        </div>
      )}

      {/* Enhanced Preview for Selected Items */}
      {selectedItems.length > 0 && (
        <Card style={{ marginTop: '16px' }}>
          <div style={{ padding: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px'
            }}
            >
              <Icon icon="eye-open" size="small" style={{ marginRight: '4px' }} />
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.enhanced.selectedPreview"
                  defaultMessage="Selected Items Preview"
                />
              </strong>
            </div>

            {/* Preview Type Selector */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
              >
                <Button
                  buttonStyle={previewType === 'user' ? 'primary' : 'default'}
                  onClick={() => setPreviewType('user')}
                  style={{ borderRadius: 0, flex: 1, fontSize: '10px' }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.barcodePrinting.previewType.user"
                    defaultMessage="User Preview"
                  />
                </Button>
                <Button
                  buttonStyle={previewType === 'professional' ? 'primary' : 'default'}
                  onClick={() => setPreviewType('professional')}
                  style={{ borderRadius: 0, flex: 1, fontSize: '10px' }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.barcodePrinting.previewType.professional"
                    defaultMessage="Technical"
                  />
                </Button>
              </div>
            </div>

            {/* Compact Preview */}
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: '#f8f9fa'
            }}
            >
              {previewType === 'user' && (
                <UserFriendlyPreview
                  items={selectedItems.slice(0, 3)}
                  settings={printSettings}
                />
              )}

              {previewType === 'professional' && (
                <ProfessionalBarcodePreview
                  items={selectedItems.slice(0, 3)}
                  settings={printSettings}
                />
              )}

              {selectedItems.length > 3 && (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#666',
                  marginTop: '8px'
                }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.barcodePrinting.enhanced.showingFirst"
                    defaultMessage="Showing first 3 of {total} selected items"
                    values={{ total: selectedItems.length }}
                  />
                </div>
              )}
            </div>

            {/* Summary Info */}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              {selectedItems.length} items × {printSettings.copies} copies = {totalLabels} labels
              <br />
              Printer: {printSettings.printerType || 'ZEBRA_ZPL'}
              <br />
              Format: {printSettings.format || 'CODE128'}
            </div>
          </div>
        </Card>
      )}

      {/* Print Button */}
      <div style={{ marginTop: '20px' }}>
        <Button
          buttonStyle="primary"
          fullWidth
          disabled={selectedItems.length === 0 || isPrinting}
          onClick={onPrint}
          loading={isPrinting}
        >
          <Icon icon="print" size="small" style={{ marginRight: '8px' }} />
          <FormattedMessage
            id="ui-plugin-inventory-templates.barcodePrinting.enhanced.printButton"
            defaultMessage="Print {count} Labels"
            values={{ count: totalLabels }}
          />
        </Button>
      </div>

      {/* Test Print Button */}
      {selectedItems.length > 0 && printSettings.printerMode === 'professional' && (
        <Button
          buttonStyle="default"
          fullWidth
          style={{ marginTop: '8px' }}
          disabled={isPrinting}
        >
          <Icon icon="lightning" size="small" style={{ marginRight: '8px' }} />
          <FormattedMessage
            id="ui-plugin-inventory-templates.barcodePrinting.enhanced.testPrint"
            defaultMessage="Test Print (1 Label)"
          />
        </Button>
      )}
    </div>
  );

  return (
    <Pane
      defaultWidth="100%"
      renderHeader={renderHeader}
    >
      {renderStatsBar()}

      <Row style={{ height: 'calc(100vh - 200px)' }}>
        {/* Main Content Area - Items Table with Integrated Barcode Preview */}
        <Col xs={8} style={{ height: '100%' }}>
          <Card>
            {renderItemsTable()}
          </Card>
        </Col>

        {/* Settings Panel */}
        <Col
          xs={4}
          style={{
            height: '100%',
            backgroundColor: '#f8f9fa',
            borderLeft: '1px solid #e9ecef'
          }}
        >
          {renderSettingsPanel()}
        </Col>
      </Row>

      <Callout ref={callout} />
    </Pane>
  );
};

EnhancedBarcodeInterface.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  instance: PropTypes.object,
  selectedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  onItemSelection: PropTypes.func.isRequired,
  printSettings: PropTypes.object.isRequired,
  onSettingsChange: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  isPrinting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedPrinter: PropTypes.object,
  onPrinterSelected: PropTypes.func.isRequired,
  discoveredPrinters: PropTypes.arrayOf(PropTypes.object), // kware: قائمة الطابعات المكتشفة
  onPrintersDiscovered: PropTypes.func, // kware: معالج اكتشاف الطابعات
};

export default EnhancedBarcodeInterface;
